import { useState, useRef, useLayoutEffect, type ComponentType, type RefObject } from 'react';
import { ChevronDown, type LucideProps } from 'lucide-react';
import { PageDecorations } from './decorative/PageDecorations';
import { ImageLightbox } from './ImageLightbox';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { useSiteStyles } from '../hooks/useSiteStyles';

type DecorationPreset = 'hero' | 'features' | 'reviews' | 'cta' | 'pricing' | 'signup' | 'minimal' | 'landing-hero' | 'landing-content' | 'seo-hero' | 'seo-content' | 'seo-features' | 'seo-reviews' | 'seo-faq' | 'seo-cta';

interface ToolPageHeroProps {
  toolName: string;
  icon: ComponentType<LucideProps>;
  pageCopy: { title: string; subtitle: string } | null;
  plan?: { credits: number } | null;
  decorationPreset?: DecorationPreset;
  gradientClasses?: string;
  badgeClasses?: string;
  beforeImage?: string;
  afterImage?: string;
  beforeImageAlt?: string;
  afterImageAlt?: string;
  isFreeUser?: boolean;
  dailyUsage?: number;
  dailyLimit?: number;
  dailyRemaining?: number;
  subtitleRef?: RefObject<HTMLDivElement>;
}

export function ToolPageHero({
  toolName,
  icon: Icon,
  pageCopy,
  plan,
  decorationPreset = 'minimal',
  gradientClasses = 'from-primary-50/30 via-white to-transparent',
  badgeClasses = 'bg-gray-100 text-gray-700',
  beforeImage,
  afterImage,
  beforeImageAlt,
  afterImageAlt,
  isFreeUser,
  dailyUsage,
  dailyLimit,
  dailyRemaining,
  subtitleRef,
}: ToolPageHeroProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasImages = beforeImage && afterImage;
  const { styles: siteStyles } = useSiteStyles();
  const decorationOpacity = siteStyles?.decoration_opacity ?? 1;

  // Measured before paint so the "Read more" button doesn't appear a frame later and
  // push the tool down.
  useLayoutEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      setNeedsExpand(height > 72);
    }
  }, [pageCopy]);

  return (
    <>
      <section className={`relative w-full overflow-hidden bg-gradient-to-b ${gradientClasses} pb-6 pt-8 md:pt-10 md:pb-8`}>
        <PageDecorations preset={decorationPreset} opacityMultiplier={0.55 * decorationOpacity} />

        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
            <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${badgeClasses}`}>
              <Icon className="w-4 h-4 mr-2" />
              {toolName}
            </div>

            {plan && (
              <div className="inline-flex items-center px-4 py-2 bg-white/80 border border-gray-200 rounded-2xl shadow-sm">
                <div className="flex items-center space-x-4">
                  <div className="text-sm">
                    <span className="text-gray-600">Credits:</span>
                    <span className="ml-1.5 font-semibold text-gray-800">{plan.credits}</span>
                  </div>
                  {isFreeUser && dailyUsage !== undefined && dailyLimit !== undefined && (
                    <div className="text-sm border-l border-gray-200 pl-4">
                      <span className="text-gray-600">Free today:</span>
                      <span className={`ml-1.5 font-semibold ${dailyRemaining === 0 ? 'text-secondary-600' : 'text-gray-800'}`}>
                        {dailyUsage}/{dailyLimit}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {pageCopy ? (
            <div className="w-full">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{pageCopy.title}</h1>

              <div className="relative">
                <div
                  ref={contentRef}
                  className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
                    expanded ? 'max-h-[2000px]' : 'max-h-[4.5rem]'
                  }`}
                >
                  <div
                    ref={subtitleRef}
                    className="prose prose-sm text-gray-600 page-copy-content max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(pageCopy.subtitle) }}
                  />

                  {hasImages && expanded && (
                    <div className="mt-4">
                      <div
                        className="relative rounded-xl overflow-hidden shadow-md border border-gray-100 bg-white cursor-pointer group hover:shadow-lg transition-shadow duration-300 inline-block max-w-md"
                        onClick={() => setLightboxOpen(true)}
                      >
                        <div className="grid grid-cols-2">
                          <div className="relative">
                            <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px] font-medium backdrop-blur-sm">
                              Before
                            </div>
                            <img
                              src={beforeImage}
                              alt={beforeImageAlt || `${toolName} - Before`}
                              loading="eager"
                              fetchPriority="high"
                              className="w-full aspect-[4/5] object-cover group-hover:scale-[1.02] transition-transform duration-300"
                            />
                          </div>
                          <div className="relative">
                            <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-primary-600/80 text-white text-[10px] font-medium backdrop-blur-sm">
                              After
                            </div>
                            <img
                              src={afterImage}
                              alt={afterImageAlt || `${toolName} - After`}
                              loading="eager"
                              fetchPriority="high"
                              className="w-full aspect-[4/5] object-cover group-hover:scale-[1.02] transition-transform duration-300"
                            />
                          </div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/10">
                          <span className="px-3 py-1.5 rounded-full bg-white/90 text-gray-800 text-xs font-medium shadow-lg">
                            Click to enlarge
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {!expanded && needsExpand && (
                  <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/90 to-transparent pointer-events-none" />
                )}
              </div>

              {needsExpand && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  {expanded ? 'Show less' : 'Read more'}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>
          ) : (
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          )}
        </div>
      </section>

      {hasImages && (
        <ImageLightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          beforeSrc={beforeImage}
          afterSrc={afterImage}
          toolName={toolName}
          beforeAlt={beforeImageAlt}
          afterAlt={afterImageAlt}
        />
      )}
    </>
  );
}
